import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  Grow,
  Paper,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LAUNCHER_INSET_BOTTOM,
  LAUNCHER_SIZE,
  PANEL_GAP,
  PANEL_MAX_HEIGHT,
  PANEL_MIN_HEIGHT,
  PANEL_TOP_CLEARANCE,
  PANEL_WIDTH,
  REDUCED_MOTION_QUERY,
  SHEET_TOP_GAP,
  Z_INDEX,
  focusRingSx,
} from "src/_modules/AiSupportChat/constants";

/** Esc closes, unless something inside handled it or an IME is composing. */
const isCloseKey = (event) =>
  event.key === "Escape" &&
  !event.defaultPrevented &&
  !event.nativeEvent?.isComposing;

/** The desktop branch only runs from `sm` up, so the `sm` launcher sizes. */
const PANEL_INSET_BOTTOM =
  LAUNCHER_INSET_BOTTOM.sm + LAUNCHER_SIZE.sm + PANEL_GAP;

/**
 * The panel container, and the only file that knows the desktop / mobile
 * layout (plan §3.3, §12: shell A, a floating panel, chosen in T19).
 *
 * - Desktop (≥ sm): a non-modal floating panel above the launcher, at its
 *   inline-end inset (`insetInlineEnd`, from the launcher). No backdrop, no
 *   scroll lock and no focus trap, so the page stays usable. It grows out of
 *   the launcher's corner and stays below the app bar unless the screen is
 *   too short; `Z_INDEX.panel` keeps it above the app bar and the Help tab
 *   then, below page modals.
 * - Mobile (xs): a bottom sheet Dialog (focus trap, scroll lock) that leaves
 *   the dimmed page visible above it; tapping there closes it.
 *
 * Esc closes both. Popovers inside (MUI Modal) stop their own Esc; other
 * widgets can keep the panel open by calling `preventDefault()` on it.
 * Both draw a clear ring around the control focused from the keyboard, and
 * declare the UI language (`<html>` has no `lang`), so screen readers read
 * the panel with the right voice.
 *
 * @param {{ open: boolean, onClose: () => void, labelledBy: string,
 *   id?: string, insetInlineEnd: number, children: React.ReactNode }} props
 */
export default function PanelShell({
  open,
  onClose,
  labelledBy,
  id,
  insetInlineEnd,
  children,
}) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
  });
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={(event) => {
          if (!event?.defaultPrevented) onClose();
        }}
        aria-labelledby={labelledBy}
        transitionDuration={reduceMotion ? 0 : undefined}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
        PaperProps={{
          id,
          lang: i18n.language,
          sx: [
            {
              m: 0,
              width: "100%",
              maxWidth: "100%",
              height: `calc(100% - ${SHEET_TOP_GAP}px)`,
              maxHeight: "none",
              borderRadius: "20px 20px 0 0",
              overflow: "hidden",
            },
            focusRingSx,
          ],
        }}
      >
        {/* A visual cue only: the sheet isn't dragged */}
        <Box
          aria-hidden="true"
          sx={{
            flex: "0 0 auto",
            alignSelf: "center",
            width: 40,
            height: 4,
            mt: 1,
            mb: 0.5,
            borderRadius: 2,
            bgcolor: "grey.400",
          }}
        />
        {children}
      </Dialog>
    );
  }

  return (
    // Grow's inline transform-origin isn't flipped by stylis-rtl (C20):
    // grow out of the launcher's corner, bottom-right in LTR, bottom-left
    // in RTL
    <Grow
      in={open}
      mountOnEnter
      unmountOnExit
      timeout={reduceMotion ? 0 : "auto"}
      style={{
        transformOrigin:
          theme.direction === "rtl" ? "bottom left" : "bottom right",
      }}
    >
      <Paper
        id={id}
        role="dialog"
        aria-labelledby={labelledBy}
        lang={i18n.language}
        elevation={12}
        onKeyDown={(event) => {
          if (isCloseKey(event)) onClose();
        }}
        sx={[
          {
            position: "fixed",
            bottom: PANEL_INSET_BOTTOM,
            insetInlineEnd,
            width: PANEL_WIDTH,
            maxWidth: `calc(100vw - ${insetInlineEnd + PANEL_GAP}px)`,
            height: `min(${PANEL_MAX_HEIGHT}px, max(${PANEL_MIN_HEIGHT}px, calc(100vh - ${PANEL_INSET_BOTTOM + PANEL_TOP_CLEARANCE}px)))`,
            maxHeight: `calc(100vh - ${PANEL_INSET_BOTTOM + PANEL_GAP}px)`,
            borderRadius: "16px",
            zIndex: Z_INDEX.panel,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          focusRingSx,
        ]}
      >
        {children}
      </Paper>
    </Grow>
  );
}
